import React, { useEffect, useMemo, useState } from 'react';
import { FaClipboardList, FaSearch, FaSpinner, FaUserGraduate, FaUserTie } from 'react-icons/fa';
import { fetchFirstSuccessful, formatShortDate, normalizeStatus, toArray } from '../utils/formExperienceApi';
import '../styles/ManageCurriculums.css';
import '../styles/ManageRanks.css';

// Real backend: GET /crud/evaluations/all -> { data: { students: {name: [...]}, preceptors: {name: [...]} } }
// (see PharmD_System/routers/crud.py get_all_evaluations), grouped by the EVALUATED
// person's name rather than by direction — flatten and re-group by direction here.
const ALL_EVALUATION_ENDPOINTS = ['/crud/evaluations/all'];

const resolveDirection = (entry) => {
    if (entry?.direction === 'preceptor_to_student') return 'preceptor-to-student';
    if (entry?.direction === 'student_to_preceptor') return 'student-to-preceptor';
    if (entry?.direction === 'assignment_evaluation') return 'assignment-evaluation';
    // Legacy rows (submitted before the `direction` field existed) infer it from who evaluated whom.
    return entry?.evaluatorType === 'preceptor' ? 'preceptor-to-student' : 'student-to-preceptor';
};

const mapAdminEntry = (entry) => {
    const direction = resolveDirection(entry);
    // Assignment evaluations are always graded by the preceptor, so the evaluator
    // is the preceptor and the evaluated party (or the work itself) is the student side.
    const evaluatorIsPreceptor = direction !== 'student-to-preceptor';
    return {
        id: entry?.evaluationId || entry?._id || entry?.id || `${direction}-${Math.random()}`,
        curriculum: entry?.curriculumName || entry?.curriculum || 'Unassigned curriculum',
        title: entry?.title || entry?.formTitle || 'Rotation Evaluation',
        status: normalizeStatus(entry?.status || 'submitted'),
        direction,
        // Form-builder rows carry source: 'form'; fixed-endpoint rows don't.
        source: entry?.source === 'form' ? 'Form' : 'Direct',
        student: evaluatorIsPreceptor ? (entry?.evaluatedName || '—') : (entry?.evaluatorName || '—'),
        preceptor: evaluatorIsPreceptor ? (entry?.evaluatorName || '—') : (entry?.evaluatedName || '—'),
        submittedAt: entry?.submitted_at || entry?.submittedAt || entry?.createdAt || null
    };
};

const normalizeAdminOverview = (payload) => {
    const data = payload?.data || payload || {};

    const splitByDirection = (mapped) => ({
        studentToPreceptor: mapped.filter((item) => item.direction === 'student-to-preceptor'),
        preceptorToStudent: mapped.filter((item) => item.direction === 'preceptor-to-student'),
        assignmentEvaluation: mapped.filter((item) => item.direction === 'assignment-evaluation')
    });

    const flatList = toArray(data?.items || data?.evaluations);
    if (flatList.length) {
        return splitByDirection(flatList.map(mapAdminEntry));
    }

    const studentsBucket = data?.students && typeof data.students === 'object' ? Object.values(data.students) : [];
    const preceptorsBucket = data?.preceptors && typeof data.preceptors === 'object' ? Object.values(data.preceptors) : [];
    return splitByDirection([...studentsBucket, ...preceptorsBucket].flat().map(mapAdminEntry));
};

const TABS = [
    { key: 'student-to-preceptor', label: 'Student → Preceptor', bucket: 'studentToPreceptor' },
    { key: 'preceptor-to-student', label: 'Preceptor → Student', bucket: 'preceptorToStudent' },
    { key: 'assignment-evaluation', label: 'Assignment Evaluations', bucket: 'assignmentEvaluation' }
];

const AllEvaluationsPage = () => {
    const [overview, setOverview] = useState({ studentToPreceptor: [], preceptorToStudent: [], assignmentEvaluation: [] });
    const [activeTab, setActiveTab] = useState('student-to-preceptor');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [curriculumFilter, setCurriculumFilter] = useState('all');

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const payload = await fetchFirstSuccessful(ALL_EVALUATION_ENDPOINTS);
                setOverview(normalizeAdminOverview(payload));
            } catch (err) {
                setError(err?.response?.data?.message || 'Unable to load evaluations');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const activeBucket = TABS.find((tab) => tab.key === activeTab)?.bucket || 'studentToPreceptor';
    const allRows = useMemo(
        () => TABS.flatMap((tab) => overview[tab.bucket] || []),
        [overview]
    );
    const activeRows = useMemo(
        () => overview[activeBucket] || [],
        [overview, activeBucket]
    );
    const allCurricula = useMemo(
        () => [...new Set(allRows.map((item) => item.curriculum))],
        [allRows]
    );

    const filtered = useMemo(() => activeRows.filter((item) => {
        if (curriculumFilter !== 'all' && item.curriculum !== curriculumFilter) return false;
        if (!search.trim()) return true;
        const term = search.toLowerCase();
        return [item.title, item.curriculum, item.student, item.preceptor, item.status]
            .some((field) => String(field || '').toLowerCase().includes(term));
    }), [activeRows, curriculumFilter, search]);

    if (loading) {
        return (
            <div className="mc-page" style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
                <FaSpinner className="mc-spin" style={{ fontSize: '3rem', color: '#4a90e2', animation: 'spin 1s linear infinite' }} />
            </div>
        );
    }

    return (
        <div className="mc-page">
            <div className="universal-white-container mc-shell">
                <div className="mc-hero">
                    <div className="mc-hero__copy">
                        <span className="mc-eyebrow">Admin</span>
                        <h1>All Evaluations</h1>
                    </div>
                    <div className="mc-hero__stats">
                        <div className="mc-stat"><strong>{overview.studentToPreceptor.length}</strong><span>Student → Preceptor</span></div>
                        <div className="mc-stat"><strong>{overview.preceptorToStudent.length}</strong><span>Preceptor → Student</span></div>
                        <div className="mc-stat"><strong>{overview.assignmentEvaluation.length}</strong><span>Assignment Evals</span></div>
                        <div className="mc-stat"><strong>{allRows.length}</strong><span>Total</span></div>
                    </div>
                </div>

                {error ? (
                    <div className="mc-empty-state">
                        <FaClipboardList />
                        <h3>Unable to load evaluations</h3>
                        <p>{error}</p>
                    </div>
                ) : (
                    <>
                        <div className="mc-steps-bar">
                            {TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    className={`mc-step ${activeTab === tab.key ? 'is-active' : ''} is-enabled`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.key === 'student-to-preceptor' && <FaUserGraduate style={{ marginRight: 6 }} />}
                                    {tab.key === 'preceptor-to-student' && <FaUserTie style={{ marginRight: 6 }} />}
                                    {tab.key === 'assignment-evaluation' && <FaClipboardList style={{ marginRight: 6 }} />}
                                    {tab.label}
                                    <span className="mc-counter-pill" style={{ marginLeft: 8 }}>{(overview[tab.bucket] || []).length}</span>
                                </button>
                            ))}
                        </div>

                        <div className="mc-toolbar">
                            <div className="mc-search-box">
                                <FaSearch />
                                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search evaluations..." />
                            </div>
                            <select className="mr-status-filter" value={curriculumFilter} onChange={(e) => setCurriculumFilter(e.target.value)}>
                                <option value="all">All curricula</option>
                                {allCurricula.map((curriculum) => (
                                    <option key={curriculum} value={curriculum}>{curriculum}</option>
                                ))}
                            </select>
                        </div>

                        {filtered.length === 0 ? (
                            <div className="mc-empty-state">
                                <FaClipboardList />
                                <h3>No evaluations found</h3>
                                <p>Nothing matches your current filters</p>
                            </div>
                        ) : (
                            <div className="mc-cards-grid">
                                {filtered.map((item) => (
                                    <div key={item.id} className="mc-curriculum-card">
                                        <div className="mc-curriculum-card__top">
                                            <div>
                                                <h4>{item.title}</h4>
                                                <p className="mc-curriculum-card__rank">{item.curriculum}</p>
                                            </div>
                                            <div className="mc-status-pill is-status-active">{item.status}</div>
                                        </div>
                                        <div className="mc-curriculum-card__meta">
                                            <span><FaUserGraduate /> Student: {item.student}</span>
                                            <span><FaUserTie /> Preceptor: {item.preceptor}</span>
                                            <span><FaClipboardList /> Source: {item.source}</span>
                                            <span>Submitted {formatShortDate(item.submittedAt)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
};

export default AllEvaluationsPage;
